import { inject, Injectable, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { forOwn, isString } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import { pick } from 'lodash';
import { fromEvent, merge, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { BrowserControlService } from '../../../cross-team-shared/misc-services/browser-control.service';
import {
  eventMatches,
  Modifier,
  resolveModifier,
} from '../../../cross-team-shared/misc-services/events/event-helper';
import { KeyboardShortcutName } from '../../../cross-team-shared/misc-services/keyboard-shortcut.service';
import { WorkspaceLoadingService } from '../../../shared/misc-services/workspace-loading.service';
import { KeyboardEventTrackingValue } from '../../../team-apis/core/event-tracking';
import { InteractionMode } from '../../../team-apis/core/interaction-modes';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { TimerService } from '../../production-workspace/timer-badge/timer.service';
import { FeatureFlagService } from '../misc-services/feature-flag.service';
import { KeyboardShortcut, keyboardShortcuts } from './keyboard-shortcuts';

export interface KeyboardShortcutEventInfo {
  shortcutName: KeyboardShortcutName;
  keyboardEvent: KeyboardEvent;
  eventTrackingValue: KeyboardEventTrackingValue;
  allowedInteractionModes: InteractionMode[];
}

@Injectable()
export class RootKeyboardShortcutService extends InjectableSuperclass {
  event$ = new Subject<KeyboardShortcutEventInfo>();
  private taskWorkspaceStore = inject(TaskWorkspaceStore);
  private timerService = inject(TimerService);
  private isWorkspaceLoading$ = inject(WorkspaceLoadingService).isLoading$;
  private matDialog = inject(MatDialog);

  constructor(
    private browserControlService: BrowserControlService,
    private featureFlagService: FeatureFlagService,
    injector: Injector,
  ) {
    super(injector);
    merge(
      fromEvent<KeyboardEvent>(document, 'keydown'),
      fromEvent<KeyboardEvent>(document, 'keyup'),
    ).subscribe((event) => this.handleKeyboardEvent(event));

    this.subscribeTo(this.getEvent$('playPause'), ({ keyboardEvent }) => {
      // Don't scroll the page
      keyboardEvent.preventDefault();
    });
  }

  getEvent$(shortcutName: KeyboardShortcutName) {
    return this.event$.pipe(
      filter((event) => event.shortcutName === shortcutName),
    );
  }

  handleKeyboardEvent(event: KeyboardEvent, event$ = this.event$) {
    if (this.shouldSkipHandlingEvent(event)) {
      return;
    }

    const matchedShortcuts = this.getMatchingShortcuts(event);
    for (const [shortcutName, keyboardShortcut] of matchedShortcuts) {
      const eventTrackingValue: KeyboardEventTrackingValue = {
        source: 'keyboard_shortcut',
        keyboard_shortcut: this.humanReadableShortcut(keyboardShortcut),
      };
      event$.next({
        shortcutName,
        keyboardEvent: event,
        eventTrackingValue,
        allowedInteractionModes: keyboardShortcut.allowedInteractionModes,
      });
    }
  }

  tooltipSuffix(shortcutName: KeyboardShortcutName) {
    return ` (${this.humanReadableShortcut(shortcutName)})`;
  }

  humanReadableShortcut(
    shortcutOrName: KeyboardShortcut | KeyboardShortcutName,
  ) {
    let shortcut: KeyboardShortcut;
    const elements = [];

    if (isString(shortcutOrName)) {
      shortcut = keyboardShortcuts[shortcutOrName].find((s) =>
        this.featureFlagMatches(s),
      )!;
    } else {
      shortcut = shortcutOrName;
    }
    for (const modifier of shortcut.modifiers) {
      if (this.getKeyForModifier(modifier) !== shortcut.key) {
        elements.push(this.humanReadableModifier(modifier));
      }
    }

    elements.push(humanReadableKey(shortcut.key));
    return elements.join('+');
  }

  humanReadableModifier(modifier: Modifier) {
    const resolvedModifier = resolveModifier(
      modifier,
      this.browserControlService.getPlatformModifier(),
    );
    if (resolvedModifier === 'metaKey') {
      return 'CMD';
    } else if (resolvedModifier === 'altKey') {
      if (this.browserControlService.isMac()) {
        return 'OPTION';
      } else {
        return 'ALT';
      }
    } else {
      return resolvedModifier
        .substring(0, resolvedModifier.length - 3)
        .toUpperCase();
    }
  }

  private shortcutMatches(event: KeyboardEvent, shortcut: KeyboardShortcut) {
    return (
      event.key.toLowerCase() === shortcut.key.toLowerCase() &&
      this.repeatMatches(event, shortcut) &&
      eventMatches(
        event,
        pick(shortcut, 'type'),
        shortcut.modifiers,
        this.browserControlService.getPlatformModifier(),
      ) &&
      this.featureFlagMatches(shortcut)
    );
  }

  private isDialogOpen() {
    return !!this.matDialog.openDialogs.length;
  }

  private repeatMatches(event: KeyboardEvent, shortcut: KeyboardShortcut) {
    return !event.repeat || shortcut.allowRepeat;
  }

  private getKeyForModifier(modifier: string) {
    switch (modifier) {
      case 'altKey':
        return 'Alt';
      case 'ctrlKey':
        return 'Control';
      case 'platform':
        return 'Meta';
      case 'shiftKey':
        return 'Shift';
      default:
        throw new Error(`Unrecognized modifier ${modifier}`);
    }
  }

  private featureFlagMatches(shortcut: KeyboardShortcut) {
    if (
      shortcut.mustHaveFeatureFlag &&
      !this.featureFlagService.hasFlag(shortcut.mustHaveFeatureFlag)
    ) {
      return false;
    }
    if (
      shortcut.mustNotHaveFeatureFlag &&
      this.featureFlagService.hasFlag(shortcut.mustNotHaveFeatureFlag)
    ) {
      return false;
    }

    return true;
  }

  private shouldSkipHandlingEvent(event: KeyboardEvent) {
    const nonRangeInput = isNonRangeInput(event.target);
    const tagEditorElement = isTagEditorElement(event.target);
    const matSelectElement = isMatSelectElement(event.target);
    // should prevent event be triggered when task is paused or stopped
    // so duration timer is not affected
    return (
      this.IsTaskLoadingOrPaused() ||
      this.isDialogOpen() ||
      nonRangeInput ||
      tagEditorElement ||
      matSelectElement
    );
  }

  private IsTaskLoadingOrPaused() {
    return (
      this.isWorkspaceLoading$.value ||
      (this.taskWorkspaceStore.state().workspace === 'production' &&
        this.timerService.getStatus() !== 'running')
    );
  }

  private getMatchingShortcuts(event: KeyboardEvent) {
    const matchedShortcuts: Array<
      [name: KeyboardShortcutName, shortcut: KeyboardShortcut]
    > = [];
    forOwn(keyboardShortcuts, (shortcutArray, name) => {
      const matchingShortcut = shortcutArray.find((shortcut) =>
        this.shortcutMatches(event, shortcut),
      );
      if (matchingShortcut) {
        matchedShortcuts.push([name, matchingShortcut]);
      }
    });
    return matchedShortcuts;
  }
}

function humanReadableKey(key: string) {
  switch (key) {
    case 'Escape':
      return 'ESC';
    case ' ':
      return 'SPACE';
    default:
      return key.toUpperCase();
  }
}

function isNonRangeInput(eventTarget: EventTarget | null) {
  return (
    (eventTarget instanceof HTMLInputElement && eventTarget.type !== 'range') ||
    eventTarget instanceof HTMLTextAreaElement ||
    eventTarget instanceof MatSelect
  );
}

function isTagEditorElement(eventTarget: EventTarget | null) {
  return (
    eventTarget instanceof Element &&
    eventTarget.closest('h3-objectless-tag-editor')
  );
}

function isMatSelectElement(eventTarget: EventTarget | null) {
  return eventTarget instanceof Element && eventTarget.closest('mat-select');
}
